import { useCallback, useEffect, useRef, useState } from "react";

import { clampImageWidth, Image as Base } from "@corca/tiptap";
import { NodeSelection } from "@tiptap/pm/state";
import { NodeViewWrapper, ReactNodeViewRenderer } from "@tiptap/react";
import { observer } from "mobx-react-lite";
import { useTranslations } from "next-intl";

import { attachUpload, disposeApi } from "@/api";
import { Icon } from "@/components/icons/Icon";
import { Sidebar } from "@/components/sidebar";
import { isLocked } from "@/components/tiptap/lock/utils";
import { downloadImage } from "@/components/tiptap/utils/downloadImage";
import { insertImageFile } from "@/components/tiptap/utils/editorActions";
import { LineWrapper } from "@/components/tiptap/utils/line-wrapper/LineWrapper";
import { resolveUploadScope, useUploadScopes } from "@/components/tiptap/utils/useUploadScopes";
import { ActiveTooltip } from "@/components/tooltip/Tooltip";
import { getImageDimensions } from "@/utils/getImageDimensions";
import { toProsemirrorHotkey } from "@/utils/hotkeys";
import { twMerge } from "@/utils/twMerge";
import { uploadFile } from "@/utils/uploadFile";
import { VALID_IMAGE_TYPES } from "@/utils/validateImageFile";

import { BlockResizerControls, BlockResizerRoot } from "./BlockResizer";
import { useGetImageUrl } from "./context/ImageUrlContext";
import { ImageViewer } from "./ImageViewer";
import { SelectedOverlay } from "./SelectedOverlay";
import { editImage, loadImageSource } from "./utils/editImage";
import { ImageOcr } from "./utils/ImageOcr";
import {
  addPendingImagePreview,
  getPendingImagePreview,
  releasePendingImagePreview,
} from "./utils/pendingImagePreviews";

import type { ImageEdit } from "./utils/editImage";
import type { UploadScope } from "@/components/tiptap/utils/uploadScope";
import type { DocumentModel } from "@/models/DocumentModel";
import type { ImageNodeAttrs } from "@corca/tiptap";
import type { Placement } from "@floating-ui/react";
import type { NodeViewProps } from "@tiptap/core";
import type { ImageOptions } from "@tiptap/extension-image";
import type { Node as ProsemirrorNode } from "@tiptap/pm/model";
import type { ReactEventHandler } from "react";

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    extendedImage: {
      appendImage: (position?: number, attrs?: { key?: string; name?: string }) => ReturnType;
      setWidth: (width: number) => ReturnType;
      setHeight: (height: number) => ReturnType;
    };
  }
}

export type ImageNodeExtensionOptions = ImageOptions & {
  doc: DocumentModel;
  uploadScope?: UploadScope;
  disableOcr?: boolean;
  disableResize?: boolean;
  className?: string;
  renderPlace?: string;
};

// Prevent infinite retry attempts - max 3 retries
const MAX_ERROR_COUNT = 3;

// Narrower images get their action buttons outside, on the side with room.
const IMAGE_ACTIONS_MIN_WIDTH = 180;

function imageActionsPlacement(width: number, alignment: string): string {
  if (width > IMAGE_ACTIONS_MIN_WIDTH) return "right-2.5";
  // A right-aligned image sits on the line's clipped right edge (sm:overflow-hidden).
  if (alignment === "right") return "-left-2.5 -translate-x-full";
  return "-right-2.5 translate-x-full";
}

// Editing rasterizes on a canvas, which would flatten SVGs and drop animation from formats that can animate.
function canRasterize(src: string, name: string | null | undefined) {
  return !src.startsWith("data:image/svg") && !/\.(svg|gif|webp|apng|avif)$/i.test(name ?? "");
}

export function nodeIsImage(node?: ProsemirrorNode): boolean {
  return node?.type.name === "image";
}

export const ImageUploadButton = observer(function ImageUploadButton({
  doc,
  uploadScope,
  className,
  tooltipPlacement,
  wrapperClassName,
  icon,
}: {
  doc: DocumentModel;
  uploadScope?: UploadScope;
  className?: string;
  tooltipPlacement?: Placement;
  wrapperClassName?: string;
  icon?: React.ReactNode;
}) {
  const t = useTranslations();
  const { tiptap } = doc.editor;

  if (!tiptap) {
    return null;
  }

  const inputRef = useRef<HTMLInputElement>(null);

  const handleFileSelected = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();
    event.stopPropagation();
    const file = event?.target?.files?.[0];
    if (file) {
      insertImageFile(doc, file, uploadScope);
    }
  };

  return (
    <>
      <Sidebar.Button
        // Sized to its content, as when the inline label wrapped it
        className={twMerge("w-auto rounded-8 h-10 min-w-[50px]", className)}
        key={t("sidebar.createBlocks.image.title")}
        title={t("sidebar.createBlocks.image.title")}
        type="button"
        onClick={() => inputRef.current?.click()}
        hintKey="image"
        tooltipPlacement={tooltipPlacement}
        wrapperClassName={wrapperClassName}
      >
        {icon || <Icon name="Image" width={24} height={24} />}
      </Sidebar.Button>
      <input
        ref={inputRef}
        className="hidden"
        tabIndex={-1}
        type="file"
        accept={VALID_IMAGE_TYPES.join(",")}
        onChange={handleFileSelected}
      />
    </>
  );
});

const ResponsiveImage = ({
  width,
  height,
  src,
  alt = "",
  onLoad,
  onError,
  selected,
}: {
  width: number;
  height: number;
  src: string;
  alt?: string;
  onLoad?: ReactEventHandler<HTMLImageElement>;
  onError?: ReactEventHandler<HTMLImageElement>;
  selected?: boolean;
}) => {
  return (
    <div className="max-w-full mr-auto">
      <div
        className={twMerge("relative max-w-full flex flex-1 items-center justify-center")}
        style={{
          width,
          aspectRatio: `${width} / ${height}`,
        }}
      >
        {selected && <SelectedOverlay />}
        {src && (
          <img
            src={src}
            alt={alt}
            onLoad={onLoad}
            onError={onError}
            width={width}
            height={height}
            className={twMerge("absolute top-0 left-0 w-full h-full object-contain")}
          />
        )}
      </div>
    </div>
  );
};

const ImageWrapper = observer(function ImageWrapper({
  node,
  updateAttributes,
  selected,
  extension,
  getPos,
  editor,
}: NodeViewProps) {
  const { doc, uploadScope, disableOcr, disableResize, className, renderPlace } =
    extension.options as ImageNodeExtensionOptions;
  const t = useTranslations("doc.image");

  const getImageUrl = useGetImageUrl();
  const {
    src: srcAttr,
    key,
    alignment,
    width,
    height,
    name,
    sourceUid,
    sourceType,
  } = node.attrs as ImageNodeAttrs;

  const { scope, sourceScope, resolveScope } = useUploadScopes(doc, uploadScope, {
    sourceUid,
    sourceType,
  });

  const [src, setSrc] = useState<string>(
    () => srcAttr || (key && getPendingImagePreview(key)) || "",
  );
  // A crop, its undo, or a collaborator's crop swaps the key; drop the old image's URL.
  const [srcKey, setSrcKey] = useState(key);
  if (srcKey !== key) {
    setSrcKey(key);
    setSrc(srcAttr || (key && getPendingImagePreview(key)) || "");
  }
  const lastErrorSrcRef = useRef<string | null>(null);
  const isRefreshingRef = useRef(false);
  const errorCountRef = useRef(0);
  const [viewerOpen, setViewerOpen] = useState(false);
  const editSourceRef = useRef<Promise<Blob> | null>(null);
  // The first click on an image selects it; only a click on an already selected image opens the viewer.
  const wasSelectedOnPointerDownRef = useRef(false);

  const showResizeDots = !!(selected && src && !disableResize && doc.editable);

  const handleImageLoad = useCallback(
    (event: React.SyntheticEvent<HTMLImageElement, Event>) => {
      let newWidth = width;
      let newHeight = height;

      if (!width) {
        const { naturalWidth } = event.currentTarget;
        newWidth = clampImageWidth(naturalWidth);
        updateAttributes({
          width: newWidth,
        });
      }
      if (!height) {
        const { naturalWidth, naturalHeight } = event.currentTarget;
        const aspect = naturalHeight / naturalWidth;
        newHeight = newWidth * aspect;
        updateAttributes({
          height: newHeight,
        });
      }
    },
    [width, height, updateAttributes],
  );

  useEffect(() => {
    lastErrorSrcRef.current = null;
    isRefreshingRef.current = false;
    errorCountRef.current = 0;
  }, [key]);

  useEffect(() => {
    return () => {
      if (key) releasePendingImagePreview(key);
    };
  }, [key]);

  /** Loads the image once per viewer session; a failed load is dropped so Apply retries it. */
  const loadEditSource = () => {
    editSourceRef.current ??= (async () => {
      // Signed URLs expire, and the displayed image may have loaded long before.
      const readableSrc = resolveScope && key ? await getImageUrl(resolveScope, key, true) : src;
      if (!readableSrc) throw new Error("No URL to read the image from");
      return loadImageSource(readableSrc);
    })().catch((error: unknown) => {
      editSourceRef.current = null;
      throw error;
    });
    return editSourceRef.current;
  };

  // iOS paints the native selection highlight above every layer, so it would cover the viewer.
  const openViewer = () => {
    editor.commands.blur();
    setViewerOpen(true);
  };

  const closeViewer = () => {
    editSourceRef.current = null;
    setViewerOpen(false);
  };

  // Images with only a `src` (pasted HTML, chat markdown) point at third-party hosts we can't read.
  const canEdit = doc.editable && !!scope && !!key && !isLocked(node) && canRasterize(src, name);

  const handleEdit = async (edit: ImageEdit) => {
    if (!scope) return;
    const file = await editImage(await loadEditSource(), edit, name || "image");
    const uploadedKey = await uploadFile(file);
    await attachUpload(scope.type, scope.uid, uploadedKey);

    addPendingImagePreview(uploadedKey, URL.createObjectURL(file));
    const { rect } = edit;
    const [turnedWidth, turnedHeight] =
      edit.rotation % 180 === 0 ? [width, height] : [height, width];
    const newWidth = turnedWidth ? clampImageWidth(Math.round(turnedWidth * rect.width)) : 0;
    const newHeight = turnedWidth
      ? Math.round((turnedHeight * rect.height * newWidth) / (turnedWidth * rect.width))
      : 0;
    updateAttributes({
      key: uploadedKey,
      src: "",
      width: newWidth,
      height: newHeight,
      sourceUid: null,
      sourceType: null,
    });
  };

  const handleImageError = useCallback(async () => {
    if (!resolveScope || !key || isRefreshingRef.current) {
      return;
    }

    if (errorCountRef.current >= MAX_ERROR_COUNT) {
      return;
    }

    const currentSrc = src;
    if (currentSrc && lastErrorSrcRef.current === currentSrc) {
      return;
    }

    lastErrorSrcRef.current = currentSrc ?? null;
    isRefreshingRef.current = true;
    errorCountRef.current += 1;

    try {
      const freshUrl = (await getImageUrl(resolveScope, key, true)) || "";
      setSrc(freshUrl);
    } finally {
      isRefreshingRef.current = false;
    }
  }, [resolveScope, getImageUrl, key, src]);

  useEffect(() => {
    return disposeApi(async (signal) => {
      if (!scope || !key) return;

      if (sourceScope && editor.isEditable) {
        try {
          const { signedUrl } = await attachUpload(
            scope.type,
            scope.uid,
            key,
            { sourceUid: sourceScope.uid, sourceType: sourceScope.type },
            { signal },
          );
          if (signal.aborted) return;
          updateAttributes({ sourceUid: null, sourceType: null });
          setSrc(signedUrl);
          return;
        } catch (error) {
          console.error("Failed to attach pasted image", error);
        }
      }

      const pendingPreview = getPendingImagePreview(key);
      if (src && src !== pendingPreview) return;

      const signedUrl = (await getImageUrl(sourceScope ?? scope, key)) || "";
      if (signal.aborted) return;

      if (pendingPreview) {
        if (!signedUrl) return;
        await getImageDimensions(signedUrl);
        if (signal.aborted) return;
        releasePendingImagePreview(key);
      }
      setSrc(signedUrl);
    });
  }, [key, src, scope, sourceScope, getImageUrl, updateAttributes, editor.isEditable]);

  const content = (
    <div className="relative flex min-w-0">
      <ResponsiveImage
        src={src}
        alt={name ?? ""}
        width={width}
        height={height}
        onLoad={handleImageLoad}
        onError={handleImageError}
        selected={renderPlace === "chat-input" && selected}
      />
      {!disableOcr && (
        <ImageOcr
          imageSelected={selected}
          imageWidth={width}
          doc={doc}
          node={node}
          getPos={getPos}
          src={src}
          imageKey={key}
          scope={resolveScope}
          editorScope={scope}
        />
      )}
    </div>
  );

  if (renderPlace === "chat-message") {
    return <NodeViewWrapper className="w-full h-full">{content}</NodeViewWrapper>;
  }

  if (renderPlace === "chat-input") {
    return <NodeViewWrapper className="w-full h-full mb-2.5">{content}</NodeViewWrapper>;
  }

  // doc editor
  return (
    <NodeViewWrapper>
      <LineWrapper doc={doc} getPos={getPos} className="group" contentClassName="min-w-0">
        <BlockResizerRoot
          enabled={showResizeDots}
          maintainAspectRatio
          className={twMerge(
            "max-w-full",
            alignment && alignment === "left" && "mr-auto",
            alignment && alignment === "right" && "ml-auto",
            alignment && alignment === "center" && "mx-auto",
          )}
          style={{
            width: width || undefined,
            aspectRatio: width && height ? `${width} / ${height}` : undefined,
          }}
          onBlockResize={(size) => {
            updateAttributes({ width: size.width, height: size.height });
          }}
        >
          <div className="w-0 h-0">&nbsp;</div>
          <BlockResizerControls
            getLabel={(index) => {
              return t(`resize-dot-label.${index}`, { index });
            }}
          />
          {src && (
            <img
              className={twMerge("cursor-pointer w-full h-full", className)}
              alt={name ?? ""}
              src={src}
              width={width}
              height={height}
              onLoad={handleImageLoad}
              onError={handleImageError}
              onPointerDown={() => {
                wasSelectedOnPointerDownRef.current = selected;
              }}
              onClick={() => {
                if (wasSelectedOnPointerDownRef.current) openViewer();
              }}
            />
          )}
          <div
            className={twMerge(
              "absolute z-10 top-2.5 flex items-center gap-2.5",
              imageActionsPlacement(width, alignment),
            )}
          >
            {!disableOcr && (
              <ImageOcr
                overlay
                imageSelected={selected}
                imageWidth={width}
                doc={doc}
                node={node}
                getPos={getPos}
                src={src}
                imageKey={key}
                scope={resolveScope}
                editorScope={scope}
              />
            )}
            {src && (
              <ActiveTooltip
                title={t("fullScreen")}
                placement="bottom"
                className={twMerge("flex", !selected && "hidden group-hover:flex")}
              >
                <button
                  type="button"
                  aria-label={t("fullScreen")}
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    openViewer();
                  }}
                  className="flex p-[5px] rounded-8 bg-black/50 hover:bg-blue-500 [--color-icon-main:var(--color-white)]"
                >
                  <Icon name="FullScreen" width={24} height={24} />
                </button>
              </ActiveTooltip>
            )}
          </div>
        </BlockResizerRoot>
      </LineWrapper>
      {viewerOpen && (
        <ImageViewer
          src={src}
          alt={name ?? ""}
          size={{ width, height }}
          onClose={closeViewer}
          onDownload={() => downloadImage(resolveScope, { key, src, name })}
          onEdit={canEdit ? handleEdit : undefined}
          // Apply reloads and reports a failed load, so a failed prefetch is dropped here.
          onEditStart={canEdit ? () => void loadEditSource().catch(() => undefined) : undefined}
        />
      )}
    </NodeViewWrapper>
  );
});

export const ImagePlugin = Base.extend<ImageNodeExtensionOptions>({
  addNodeView() {
    return ReactNodeViewRenderer(ImageWrapper, {
      className: "w-full mx-auto max-w-content-width sm:max-w-full",
    });
  },

  addKeyboardShortcuts() {
    return {
      [toProsemirrorHotkey("save-image")]: ({ editor }) => {
        const { selection } = editor.state;
        if (!(selection instanceof NodeSelection) || !nodeIsImage(selection.node)) {
          return false;
        }
        const attrs = selection.node.attrs as ImageNodeAttrs;
        void downloadImage(
          resolveUploadScope(this.options.doc, this.options.uploadScope, attrs),
          attrs,
        );
        return true;
      },
    };
  },

  addCommands() {
    return {
      appendImage:
        (position, attrs) =>
        ({ commands, state }) => {
          return commands.insertContentAt(
            position !== undefined ? position : state.selection.$to.end(1),
            {
              type: this.name,
              attrs,
            },
          );
        },
      setWidth:
        (width: number) =>
        ({ commands }) => {
          return commands.updateAttributes("image", { width });
        },

      setHeight:
        (height: number) =>
        ({ commands }) => {
          return commands.updateAttributes("image", { height });
        },
    };
  },
});
